import React, { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import ModalBody from 'reactstrap/lib/ModalBody';
import Modal from 'reactstrap/lib/Modal';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useStateCallback } from 'site-modules/shared/hooks/use-state-callback';

import { getFullImageUrl, getS3MediaDefaultImageUrlBySize } from 'client/utils/image-helpers';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { MAX_CARS_TO_COMPARE } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { VEHICLE_CARD_PHOTO_SIZE, VEHICLE_CAROUSEL_PHOTO_WIDTH } from 'site-modules/editorial/constants/best';
import { HeroCarousel } from 'site-modules/shared/components/hero-carousel/hero-carousel';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { TitleLink } from './title-link';

const PHOTO_BUTTON_CLASS_NAME = 'photo-button pos-r w-100 p-0 border-0';

export function CardPhoto({
  vehicleParams: { make, model, year, style },
  photoLinks,
  titleLink,
  subtitle,
  selectedCars,
  number = null,
  isMobile = false,
  ariaId,
  onSelectCar,
}) {
  const selected = selectedCars.some(({ id }) => id === style);
  const selectedCarsNumber = selectedCars.length;
  const [isPhotoCarouselOpen, togglePhotoCarouselOpen] = useToggle(false);
  const [checked, setChecked] = useStateCallback(selected);

  const photoAlt = `${year} ${make} ${model}`;
  const photoUrl = photoLinks.length
    ? getFullImageUrl(photoLinks[0].href, VEHICLE_CARD_PHOTO_SIZE.width)
    : getS3MediaDefaultImageUrlBySize({
        make: { slug: makeNiceName(make) },
        model: { slug: makeNiceName(model) },
        year,
      });

  const handleCompareClick = useCallback(
    event => {
      function callback() {
        onSelectCar(event, { id: style, styleId: style, image: { url: photoUrl, alt: photoAlt } });
      }
      if (selectedCarsNumber >= MAX_CARS_TO_COMPARE && !checked) {
        callback();
        return;
      }
      setChecked(!checked, () => {
        callback();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checked, onSelectCar, photoAlt, photoUrl, selectedCarsNumber, style]
  );

  useEffect(() => {
    setChecked(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const photoSlides = photoLinks.map(({ href }) => (
    <img src={getFullImageUrl(href, VEHICLE_CAROUSEL_PHOTO_WIDTH)} alt={photoAlt} loading={'lazy'} />
  ));

  const vehiclePhotoButtonContent = (
    <Fragment>
      <img className="w-100" src={photoUrl} alt={photoAlt} loading="lazy" />
      <i className="icon icon-camera small text-white rounded p-0_5 mt-0_5 me-0_5" aria-hidden />
      <span className="visually-hidden">View more photos</span>
    </Fragment>
  );

  const checkBoxId = `${ariaId}-${style}`;

  return (
    <Row className="vehicle-photo">
      <Col xs={5} md={12}>
        {photoSlides.length ? (
          <Button
            className={PHOTO_BUTTON_CLASS_NAME}
            onClick={togglePhotoCarouselOpen}
            data-tracking-id="open_legacy_photoflipper"
          >
            {vehiclePhotoButtonContent}
          </Button>
        ) : (
          <PhotoflipperButtonAsync makeSlug={makeNiceName(make)} modelSlug={makeNiceName(model)} year={year}>
            <Button className={PHOTO_BUTTON_CLASS_NAME} style={{ aspectRatio: '3/2' }}>
              {vehiclePhotoButtonContent}
            </Button>
          </PhotoflipperButtonAsync>
        )}
        <Checkbox
          id={checkBoxId}
          labelText="Compare"
          containerClassName="compare-checkbox d-flex align-items-center text-gray-darker size-14 rounded px-1 mt-1"
          labelClassName="compare-label"
          customLabelClassName="rounded"
          textLabelClassName="ps-1_5"
          onChange={handleCompareClick}
          checked={selectedCars.some(({ id }) => id === style)}
          autoComplete="off"
          data-hidedelightedoninteraction
          aria-labelledby={classnames(`checkbox-${checkBoxId}`, ariaId)}
        />
        {!!photoSlides.length && (
          <Modal
            className="vehicle-card-carousel-modal"
            isOpen={isPhotoCarouselOpen}
            toggle={togglePhotoCarouselOpen}
            centered
            size="lg"
          >
            <ModalHeader tag="div" className="justify-content-end p-0" onClick={togglePhotoCarouselOpen}>
              <Button color="link" className="text-decoration-none p-0_25 heading-3 text-white">
                <i className="icon-cross2" aria-hidden />
              </Button>
            </ModalHeader>
            <ModalBody className="p-0">
              <HeroCarousel
                autoplay={false}
                lazyLoad
                className="editorial-hero-carousel"
                data-tracking-parent="edm-entry-editorial-hero-carousel"
                slides={photoSlides}
              />
            </ModalBody>
          </Modal>
        )}
      </Col>
      {isMobile && (
        <Col xs={7} className="pt-1 pe-1_5">
          <TitleLink
            subtitle={subtitle}
            titleLink={titleLink}
            headingClassName={classnames({ 'pe-1_5': number })}
            ariaId={ariaId}
          />
          {number && <div className="number heading-4 bg-gray-lighter px-0_5 py-0_25 me-0_5">{number}</div>}
        </Col>
      )}
    </Row>
  );
}

CardPhoto.propTypes = {
  vehicleParams: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    style: PropTypes.string,
  }).isRequired,
  titleLink: PropTypes.shape({
    url: PropTypes.string,
    blank: PropTypes.bool,
    nofollow: PropTypes.bool,
    title: PropTypes.string,
  }).isRequired,
  subtitle: PropTypes.string.isRequired,
  photoLinks: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
    })
  ).isRequired,
  selectedCars: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectCar: PropTypes.func.isRequired,
  number: PropTypes.number,
  isMobile: PropTypes.bool,
  ariaId: PropTypes.string.isRequired,
};
